import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import api from '../../services/api';
import { format } from 'date-fns';
import { parseLanguageCsv } from '../../utils/languageOptions';
import { normalizeApiError } from '../../utils/errorMessages';
import {
  Calendar, Clock, Users, Search, Video,
  ChevronRight, Archive, Zap, PlayCircle, Sparkles, Play,
} from 'lucide-react';

/* ── helpers ─────────────────────────────────── */
const STATUS_CONFIG = {
  ONGOING:   { label: 'Live Now',  color: '#10b981', bg: '#ecfdf5', dot: true },
  SCHEDULED: { label: 'Upcoming',  color: '#6366f1', bg: '#eef2ff', dot: false },
  COMPLETED: { label: 'Ended',     color: '#64748b', bg: '#f1f5f9', dot: false },
  CANCELLED: { label: 'Cancelled', color: '#ef4444', bg: '#fef2f2', dot: false },
  ARCHIVED:  { label: 'Archived',  color: '#94a3b8', bg: '#f8fafc', dot: false },
};

const isUpcoming = (m) => ['SCHEDULED', 'ONGOING'].includes(m.status) && m.isActive !== false;
const getLanguageList = (meeting) => parseLanguageCsv(meeting?.language);

/* ── component ───────────────────────────────── */
const UserMeetings = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [doctorFilter, setDoctorFilter] = useState('');
  const [topicFilter, setTopicFilter] = useState('');
  const [languageFilter, setLanguageFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [joiningId, setJoiningId] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => { fetchMeetings(); }, []);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const fetchMeetings = async () => {
    try {
      const res = await api.get('/meetings');
      setMeetings(res.data);
    } catch (e) {
      console.error('Failed to fetch meetings:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async (meeting) => {
    try {
      setJoiningId(meeting.id);
      const res = await api.post('/meetings/secure/generate-join-link', { meetingId: meeting.id });
      if (res.data.joinLink) {
        sessionStorage.setItem(`meeting_token_${meeting.id}`, res.data.token);
        navigate(`/meeting/join?meetingId=${meeting.id}&token=${res.data.token}`);
      }
    } catch (e) {
      alert(normalizeApiError(e, 'Failed to join meeting'));
    } finally {
      setJoiningId(null);
    }
  };

  const handlePlayArchive = (meeting) => {
    const archiveSource = meeting.archiveRecordingUrl || meeting.youtubeStreamUrl;
    if (!archiveSource) {
      return;
    }

    navigate(`/meeting/stream/${meeting.id}`, {
      state: {
        streamEmbedUrl: archiveSource,
        meetingTitle: `${meeting.title} (Recording)`,
      },
    });
  };

  const getSpeakerNames = (m) =>
    (m.speakers || []).map((s2) => s2.speaker?.name || s2.name).filter(Boolean).join(', ');

  const matchFilters = (m) => {
    const q = searchTerm.toLowerCase();
    const dateLabel = format(new Date(m.startTime), 'yyyy-MM-dd');
    const doctorNames = getSpeakerNames(m).toLowerCase();
    const topic = (m.topic || m.title || '').toLowerCase();
    const languages = getLanguageList(m).map((item) => item.toLowerCase());

    const matchesSearch =
      !q ||
      m.title.toLowerCase().includes(q) ||
      m.description?.toLowerCase().includes(q) ||
      topic.includes(q);

    const matchesDoctor = !doctorFilter || doctorNames.includes(doctorFilter.toLowerCase());
    const matchesTopic = !topicFilter || topic.includes(topicFilter.toLowerCase());
    const matchesLanguage = languageFilter === 'all' || languages.includes(languageFilter.toLowerCase());
    const matchesDate = !dateFilter || dateLabel === dateFilter;

    return matchesSearch && matchesDoctor && matchesTopic && matchesLanguage && matchesDate;
  };

  const availableLanguages = Array.from(
    new Set(meetings.flatMap((meeting) => getLanguageList(meeting)))
  ).sort((a, b) => a.localeCompare(b));

  const upcoming = meetings.filter(m => isUpcoming(m) && matchFilters(m));
  const archived  = meetings.filter(m => !isUpcoming(m) && matchFilters(m))
    .sort((a, b) => new Date(b.startTime) - new Date(a.startTime));

  /* ── styles ──────────────────────── */
  const s = {
    page: { padding: isMobile ? '16px 14px 28px' : '28px 24px', maxWidth: '1200px', margin: '0 auto' },
    mobileHero: {
      display: isMobile ? 'block' : 'none',
      borderRadius: '16px',
      padding: '14px 14px 12px',
      marginBottom: '14px',
      border: `1px solid ${theme.colors.border}`,
      background: `linear-gradient(125deg, ${theme.colors.cardBg}, ${theme.colors.backgroundSecondary})`,
    },
    mobileHeroTitle: { fontSize: '15px', fontWeight: '700', color: theme.colors.text, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' },
    mobileStatGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' },
    mobileStatCard: {
      borderRadius: '10px',
      border: `1px solid ${theme.colors.border}`,
      backgroundColor: theme.colors.cardBg,
      padding: '8px',
      textAlign: 'center',
    },
    mobileStatValue: { fontSize: '16px', fontWeight: '700', color: theme.colors.text },
    mobileStatLabel: { fontSize: '11px', color: theme.colors.textSecondary, marginTop: '2px' },
    pageTitle: { fontSize: '24px', fontWeight: '700', color: theme.colors.text, marginBottom: '4px' },
    pageSub: { fontSize: '14px', color: theme.colors.textSecondary, marginBottom: '24px' },
    searchWrap: {
      position: 'relative', maxWidth: '440px', marginBottom: isMobile ? '18px' : '32px',
    },
    searchIcon: { position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: theme.colors.textTertiary, pointerEvents: 'none' },
    searchInput: {
      width: '100%', padding: '10px 14px 10px 40px',
      borderRadius: '10px', border: `1px solid ${theme.colors.border}`,
      backgroundColor: theme.colors.cardBg, color: theme.colors.text,
      fontSize: '14px', outline: 'none', transition: 'border-color 0.2s, box-shadow 0.2s',
      boxSizing: 'border-box',
    },
    filtersGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, minmax(0, 1fr))',
      gap: '10px',
      marginBottom: isMobile ? '18px' : '24px',
    },
    filterInput: {
      width: '100%',
      padding: '10px 12px',
      borderRadius: '10px',
      border: `1px solid ${theme.colors.border}`,
      backgroundColor: theme.colors.cardBg,
      color: theme.colors.text,
      fontSize: '13px',
      outline: 'none',
      boxSizing: 'border-box',
    },
    sectionLabel: {
      fontSize: '11px', fontWeight: '700', textTransform: 'uppercase',
      letterSpacing: '0.8px', color: theme.colors.textTertiary, marginBottom: isMobile ? '10px' : '14px',
      display: 'flex', alignItems: 'center', gap: '8px',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
      gap: '14px',
      marginBottom: '40px',
    },
    card: {
      backgroundColor: theme.colors.cardBg,
      border: `1px solid ${theme.colors.border}`,
      borderRadius: '14px',
      overflow: 'hidden',
      transition: 'transform 0.2s, box-shadow 0.2s',
      display: 'flex', flexDirection: 'column',
    },
    cardInner: { padding: '16px', flex: 1, display: 'flex', flexDirection: 'column' },
    cardTopRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' },
    cardTitle: { fontSize: '15px', fontWeight: '600', color: theme.colors.text, marginBottom: '8px', lineHeight: '1.35' },
    cardMeta: { fontSize: '12px', color: theme.colors.textSecondary, display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '5px' },
    cardDesc: { fontSize: '12px', color: theme.colors.textSecondary, lineHeight: '1.45', marginTop: '6px', marginBottom: '12px', flex: 1,
      display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' },
    divider: { height: '1px', backgroundColor: theme.colors.border, margin: '0 -16px 12px' },
    btn: (variant) => {
      const map = {
        join: { bg: 'linear-gradient(90deg, #16a34a, #22c55e)', color: '#fff', border: 'none' },
        upcoming: { bg: theme.colors.primary, color: '#fff', border: 'none' },
        cancelled: { bg: 'transparent', color: theme.colors.textTertiary, border: `1px solid ${theme.colors.border}` },
      };
      const v = map[variant] || map.upcoming;
      return {
        width: '100%', padding: '10px 16px', borderRadius: '9px',
        fontWeight: '600', fontSize: '14px', cursor: variant === 'cancelled' ? 'not-allowed' : 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
        transition: 'all 0.2s', ...v, opacity: variant === 'cancelled' ? 0.6 : 1,
        appearance: 'none',
        WebkitAppearance: 'none',
        background: v.bg,
        backgroundColor: typeof v.bg === 'string' && v.bg.startsWith('linear-gradient') ? undefined : v.bg,
        boxShadow: variant === 'join' ? '0 8px 18px rgba(34, 197, 94, 0.26)' : 'none',
      };
    },
    // archive table
    archiveSection: { marginTop: '8px' },
    archiveHeader: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' },
    archiveTitle: { fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.8px', color: theme.colors.textTertiary },
    tableWrap: {
      backgroundColor: 'transparent',
      border: 'none',
      borderRadius: 0,
      overflow: 'hidden',
    },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: {
      padding: '12px 16px', textAlign: 'left', fontSize: '11px', fontWeight: '700',
      textTransform: 'uppercase', letterSpacing: '0.5px', color: theme.colors.textSecondary,
      backgroundColor: theme.colors.backgroundSecondary, borderBottom: `1px solid ${theme.colors.border}`,
      whiteSpace: 'nowrap',
    },
    td: { padding: '13px 16px', fontSize: '14px', color: theme.colors.text, borderBottom: `1px solid ${theme.colors.borderLight}`, verticalAlign: 'middle' },
    tdSub: { fontSize: '12px', color: theme.colors.textSecondary, marginTop: '2px' },
    archivePlayBtn: (enabled) => ({
      border: enabled ? `1px solid ${theme.colors.primary}` : `1px solid ${theme.colors.border}`,
      borderRadius: '8px',
      padding: '6px 10px',
      fontSize: '12px',
      fontWeight: '600',
      backgroundColor: enabled ? `${theme.colors.primary}12` : 'transparent',
      color: enabled ? theme.colors.primary : theme.colors.textTertiary,
      cursor: enabled ? 'pointer' : 'not-allowed',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
    }),
    mobileArchiveList: {
      display: isMobile ? 'grid' : 'none',
      gridTemplateColumns: '1fr',
      gap: '10px',
    },
    mobileArchiveCard: {
      borderRadius: '12px',
      border: `1px solid ${theme.colors.border}`,
      padding: '12px',
      backgroundColor: theme.colors.cardBg,
    },
    mobileArchiveTitle: { fontSize: '14px', fontWeight: '600', color: theme.colors.text, marginBottom: '8px' },
    mobileArchiveMeta: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: theme.colors.textSecondary, marginBottom: '6px' },
  };

  const StatusPill = ({ status }) => {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.SCHEDULED;
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: '5px',
        padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600',
        backgroundColor: cfg.bg, color: cfg.color,
      }}>
        {cfg.dot && (
          <span style={{
            width: '6px', height: '6px', borderRadius: '50%', backgroundColor: cfg.color,
            animation: 'pulse 1.5s infinite',
          }} />
        )}
        {cfg.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div style={{ ...s.page, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '50%', border: `3px solid ${theme.colors.primaryLight}`, borderTopColor: theme.colors.primary, animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
          <p style={{ color: theme.colors.textSecondary, fontSize: '14px' }}>Loading your meetings…</p>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
        .meeting-card:hover { transform: translateY(-3px); box-shadow: 0 8px 24px rgba(99,102,241,0.12); }
        .join-btn:hover:not(:disabled) { filter: brightness(1.08); transform: translateY(-1px); }
        .join-btn:disabled { filter: none; }
        .search-input:focus { border-color: #6366f1 !important; box-shadow: 0 0 0 3px rgba(99,102,241,0.12) !important; }
        @media (max-width: 768px) {
          .meetings-grid { grid-template-columns: 1fr !important; }
          .desktop-archive-table { display: none; }
          .meeting-card:hover { transform: none; box-shadow: none; }
          .join-btn { min-height: 42px; }
        }
      `}</style>

      <div style={s.mobileHero}>
        <div style={s.mobileHeroTitle}>
          <Sparkles size={14} />
          Today at a glance
        </div>
        <div style={s.mobileStatGrid}>
          <div style={s.mobileStatCard}>
            <div style={s.mobileStatValue}>{upcoming.length}</div>
            <div style={s.mobileStatLabel}>Upcoming</div>
          </div>
          <div style={s.mobileStatCard}>
            <div style={s.mobileStatValue}>{upcoming.filter((m) => m.status === 'ONGOING').length}</div>
            <div style={s.mobileStatLabel}>Live</div>
          </div>
          <div style={s.mobileStatCard}>
            <div style={s.mobileStatValue}>{archived.length}</div>
            <div style={s.mobileStatLabel}>Past</div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div style={s.searchWrap}>
        <Search size={16} style={s.searchIcon} />
        <input
          className="search-input"
          style={s.searchInput}
          type="text"
          placeholder="Search by title or description…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div style={s.filtersGrid}>
        <input
          style={s.filterInput}
          type="text"
          placeholder="Doctor"
          value={doctorFilter}
          onChange={(e) => setDoctorFilter(e.target.value)}
        />
        <input
          style={s.filterInput}
          type="text"
          placeholder="Topic"
          value={topicFilter}
          onChange={(e) => setTopicFilter(e.target.value)}
        />
        <input
          style={s.filterInput}
          type="date"
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
        />
        <select
          style={s.filterInput}
          value={languageFilter}
          onChange={(e) => setLanguageFilter(e.target.value)}
        >
          <option value="all">All Languages</option>
          {availableLanguages.map((language) => (
            <option key={language} value={language.toLowerCase()}>{language}</option>
          ))}
        </select>
      </div>

      {/* ── Upcoming meetings cards ── */}
      <div style={s.sectionLabel}>
        <Zap size={13} />
        Upcoming &amp; Live
        <span style={{ fontWeight: '400', color: theme.colors.textTertiary, textTransform: 'none', letterSpacing: 0 }}>
          ({upcoming.length})
        </span>
      </div>

      {upcoming.length === 0 ? (
        <div style={{
          backgroundColor: theme.colors.cardBg, border: `1px solid ${theme.colors.border}`,
          borderRadius: '14px', padding: '48px 24px', textAlign: 'center', marginBottom: '40px',
        }}>
          <Video size={40} style={{ color: theme.colors.textTertiary, margin: '0 auto 12px' }} />
          <p style={{ color: theme.colors.text, fontWeight: '600', marginBottom: '6px' }}>No upcoming meetings</p>
          <p style={{ color: theme.colors.textSecondary, fontSize: '14px' }}>
            {searchTerm ? 'Try a different search.' : 'You have no upcoming meetings scheduled.'}
          </p>
        </div>
      ) : (
        <div className="meetings-grid" style={s.grid}>
          {upcoming.map((meeting) => {
            const isLive = meeting.status === 'ONGOING';
            const isCancelled = meeting.status === 'CANCELLED';
            const isJoining = joiningId === meeting.id;
            return (
              <div
                key={meeting.id}
                className="meeting-card"
                style={s.card}
              >
                {/* Accent stripe */}
                <div style={{
                  height: '4px',
                  background: isLive
                    ? `linear-gradient(90deg, ${theme.colors.success}, #34d399)`
                    : `linear-gradient(90deg, ${theme.colors.primary}, #818cf8)`,
                }} />
                <div style={s.cardInner}>
                  <div style={s.cardTopRow}>
                    <StatusPill status={meeting.status} />
                    {meeting.bannerUrl && (
                      <img src={meeting.bannerUrl} alt="" style={{ width: '44px', height: '44px', borderRadius: '8px', objectFit: 'cover' }} />
                    )}
                  </div>

                  <div style={s.cardTitle}>{meeting.title}</div>

                  <div style={s.cardMeta}>
                    <Calendar size={13} style={{ flexShrink: 0 }} />
                    {format(new Date(meeting.startTime), 'EEE, MMM d, yyyy')}
                  </div>
                  <div style={s.cardMeta}>
                    <Clock size={13} style={{ flexShrink: 0 }} />
                    {format(new Date(meeting.startTime), 'h:mm a')}
                  </div>
                  {meeting.speakers?.length > 0 && (
                    <div style={s.cardMeta}>
                      <Users size={13} style={{ flexShrink: 0 }} />
                      {getSpeakerNames(meeting)}
                    </div>
                  )}
                  {meeting.topic && <div style={s.cardMeta}>Topic: {meeting.topic}</div>}
                  {meeting.language && <div style={s.cardMeta}>Languages: {getLanguageList(meeting).join(', ')}</div>}
                  {meeting.description && <p style={s.cardDesc}>{meeting.description}</p>}

                  <div style={s.divider} />

                  <button
                    className="join-btn"
                    style={s.btn(isCancelled ? 'cancelled' : isLive ? 'join' : 'upcoming')}
                    onClick={() => !isCancelled && handleJoin(meeting)}
                    disabled={isCancelled || isJoining}
                  >
                    {isJoining ? (
                      <span style={{ width: '16px', height: '16px', borderRadius: '50%', border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', animation: 'spin 0.7s linear infinite', display: 'inline-block' }} />
                    ) : isLive ? (
                      <><PlayCircle size={16} /> Join Now</>
                    ) : isCancelled ? (
                      'Cancelled'
                    ) : (
                      <><ChevronRight size={16} /> Register / Join</>
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Archive table ── */}
      {archived.length > 0 && (
        <div style={s.archiveSection}>
          <div style={s.archiveHeader}>
            <Archive size={13} style={{ color: theme.colors.textTertiary }} />
            <span style={s.archiveTitle}>Past Meetings</span>
            <span style={{ fontSize: '11px', color: theme.colors.textTertiary }}>({archived.length})</span>
          </div>
          <div className="desktop-archive-table" style={s.tableWrap}>
            <div className="table-scroll-wrap">
              <table className="archive-table" style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Meeting</th>
                    <th style={s.th}>Date</th>
                    <th style={s.th}>Speakers</th>
                    <th style={s.th}>Status</th>
                    <th style={s.th}>Recording</th>
                  </tr>
                </thead>
                <tbody>
                  {archived.map((meeting, i) => (
                    <tr key={meeting.id} style={{ backgroundColor: i % 2 === 0 ? 'transparent' : theme.colors.backgroundSecondary + '50' }}>
                      <td style={s.td}>
                        <div style={{ fontWeight: '600', color: theme.colors.text }}>{meeting.title}</div>
                        {meeting.description && (
                          <div style={{ ...s.tdSub, maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {meeting.description}
                          </div>
                        )}
                      </td>
                      <td style={s.td}>
                        <div>{format(new Date(meeting.startTime), 'MMM d, yyyy')}</div>
                        <div style={s.tdSub}>{format(new Date(meeting.startTime), 'h:mm a')}</div>
                      </td>
                      <td style={s.td}>
                        {meeting.speakers?.length > 0
                          ? getSpeakerNames(meeting)
                          : <span style={{ color: theme.colors.textTertiary }}>—</span>}
                      </td>
                      <td style={s.td}>
                        <StatusPill status={meeting.status} />
                      </td>
                      <td style={s.td}>
                        <button
                          type="button"
                          style={s.archivePlayBtn(Boolean(meeting.archiveRecordingUrl))}
                          onClick={() => handlePlayArchive(meeting)}
                          disabled={!meeting.archiveRecordingUrl}
                        >
                          <Play size={12} />
                          Play
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div style={s.mobileArchiveList}>
            {archived.map((meeting) => (
              <div key={`mobile-${meeting.id}`} style={s.mobileArchiveCard}>
                <div style={s.mobileArchiveTitle}>{meeting.title}</div>
                <div style={s.mobileArchiveMeta}>
                  <Calendar size={12} style={{ flexShrink: 0 }} />
                  {format(new Date(meeting.startTime), 'MMM d, yyyy • h:mm a')}
                </div>
                {meeting.speakers?.length > 0 && (
                  <div style={s.mobileArchiveMeta}>
                    <Users size={12} style={{ flexShrink: 0 }} />
                    {getSpeakerNames(meeting)}
                  </div>
                )}
                <StatusPill status={meeting.status} />
                <div style={{ marginTop: '10px' }}>
                  <button
                    type="button"
                    style={s.archivePlayBtn(Boolean(meeting.archiveRecordingUrl))}
                    onClick={() => handlePlayArchive(meeting)}
                    disabled={!meeting.archiveRecordingUrl}
                  >
                    <Play size={12} />
                    Play Recording
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserMeetings;
